import { FunctionComponent } from "react";
import { debounce } from "lodash";

import { WhitelabelSettingReadModel } from "../../../../../../../@api";

import { appSettingsKeys } from "../../../../../../models/app-setting-keys";
import { translationKeys } from "../../../../../../models/app-translation-keys";
import { applyCSSSetting } from "../../../../../../models/functions/apply-css-setting.function";
import { useWhitelabelService } from "../../../../../../services/ServiceHooks/whitelabelHook";
import { SettingList } from "../../Layout/SettingList/SettingList";
import { ICSSSettingListItem } from "../../Models/interfaces/css-setting-list-item.interface";
import { ISettingListItem } from "../../Models/interfaces/setting-list-item.interface";
import { SettingColorPicker } from "../../SettingLibrary/ColorPicker/SettingColorPicker";

import "./ColorSettings.scss";

interface IColorSettingsProps {
  settings: WhitelabelSettingReadModel[];
  resetSetting: (key: string) => void;
}

export const ColorSettings: FunctionComponent<IColorSettingsProps> = (props: IColorSettingsProps) => {
  const whitelabelService = useWhitelabelService();

  const colorSettingTranslationsKeys = translationKeys.adminArea.whiteLabel.areas.color.settings;

  const settingHexColorItemTemplate = (
    setting: ICSSSettingListItem,
    whitelabelSetting: WhitelabelSettingReadModel | undefined,
  ) => {
    const customValue = whitelabelSetting?.customValue;
    const color = customValue?.trim() ? customValue : (whitelabelSetting?.defaultValue ?? "#000000");

    return <SettingColorPicker value={color} onChange={color => onChange(setting, color)} />;
  };

  const gradientBackgroundExample = (value: any) => (
    <div
      style={{
        width: "6rem",
        height: "1.5rem",
        background: value,
        borderRadius: "var(--customer-border-radius-small)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "#cccccc",
      }}
    />
  );

  const settingList: ICSSSettingListItem[] = [
    {
      settingKey: appSettingsKeys["Color.BrandColorBackground"],
      labelKey: colorSettingTranslationsKeys.brandColorBackground.label,
      cssKeys: ["brand-color-background"],
      cssPrefix: "customer-",
      centerVertically: true,
      hasReset: true,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingHexColorItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
    {
      settingKey: appSettingsKeys["Color.BrandColorForeground"],
      labelKey: colorSettingTranslationsKeys.brandColorForeground.label,
      cssKeys: ["brand-color-foreground"],
      cssPrefix: "customer-",
      centerVertically: true,
      hasReset: true,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingHexColorItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
    {
      settingKey: appSettingsKeys["Color.BrandColor"],
      labelKey: colorSettingTranslationsKeys.brandColor.label,
      cssKeys: ["brand-color"],
      cssPrefix: "customer-",
      centerVertically: true,
      hasReset: true,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingHexColorItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
    {
      settingKey: appSettingsKeys["Color.BrandColorInvalid"],
      labelKey: colorSettingTranslationsKeys.brandColorInvalid.label,
      cssKeys: ["brand-color-invalid"],
      cssPrefix: "customer-",
      centerVertically: true,
      hasReset: true,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingHexColorItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
    {
      settingKey: appSettingsKeys["Color.ButtonColorBackground"],
      labelKey: colorSettingTranslationsKeys.buttonColorBackground.label,
      cssKeys: ["button-color-background"],
      cssPrefix: "customer-",
      centerVertically: true,
      hasReset: true,
      exampleContent: gradientBackgroundExample,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingHexColorItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
    {
      settingKey: appSettingsKeys["Color.ButtonColorForeground"],
      labelKey: colorSettingTranslationsKeys.buttonColorForeground.label,
      cssKeys: ["button-color-foreground"],
      cssPrefix: "customer-",
      centerVertically: true,
      hasReset: true,
      exampleContent: gradientBackgroundExample,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingHexColorItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
    {
      settingKey: appSettingsKeys["Color.LinkColor"],
      labelKey: colorSettingTranslationsKeys.linkColor.label,
      cssKeys: ["link-color"],
      cssPrefix: "customer-",
      centerVertically: true,
      hasReset: true,
      exampleContent: <a href="example">Example Link</a>,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingHexColorItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
    {
      settingKey: appSettingsKeys["Color.TextColor"],
      labelKey: colorSettingTranslationsKeys.textColor.label,
      cssKeys: ["text-color"],
      cssPrefix: "customer-",
      centerVertically: true,
      hasReset: true,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingHexColorItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
  ];

  const onChange = (setting: ICSSSettingListItem, newCSSValue: string) => {
    setting.applyValueFunction(setting, newCSSValue);
    debounceBackendUpdateChange(setting, newCSSValue);
  };

  const debounceBackendUpdateChange = debounce(
    (setting: ISettingListItem, value) => setting.updateFunction(setting.settingKey, value),
    300,
  );

  const resetSetting = async (setting: ICSSSettingListItem) => {
    setting.applyValueFunction(setting, undefined);
    props.resetSetting(setting.settingKey);
  };

  return <SettingList list={settingList} whiteLabelSettings={props.settings} resetSetting={resetSetting} />;
};
